import Dexie, { type Table } from 'dexie';
import type { LeitourDatabase } from '../database/db';
import type { AppSettings, Book, ReadingProgressEntry, ReadingSession } from '../domain/entities/types';
import { hashSyncPayloadForEntity, payloadVersionForSyncPayload, projectReadingProgressEntryForSync, projectSyncPayload } from './canonical';
import type {
  SyncChange,
  SyncConflict,
  SyncEntityType,
  SyncPayloadMap,
  SyncSnapshotEntity
} from './types';
import { canonicalUserPreferencePayload } from './legacyPreferenceRecovery';

export interface LocalSyncEntity<E extends SyncEntityType = SyncEntityType> {
  entityType: E;
  entityId: string;
  payload: SyncPayloadMap[E];
  payloadVersion: 1 | 2;
  payloadHash: string;
}

export const syncTransactionTables = (db: LeitourDatabase) => [
  db.books, db.sessions, db.progressEntries, db.statusHistory, db.notes, db.quotes,
  db.series, db.goals, db.settings, db.syncOutbox, db.syncEntityStates,
  db.syncConflicts, db.syncMetadata
];

const tableFor = (db: LeitourDatabase, entityType: Exclude<SyncEntityType, 'user_preference'>): Table => {
  switch (entityType) {
    case 'book': return db.books;
    case 'series': return db.series;
    case 'note': return db.notes;
    case 'quote': return db.quotes;
    case 'goal': return db.goals;
    case 'reading_session': return db.sessions;
    case 'reading_progress_entry': return db.progressEntries;
    case 'book_status_history': return db.statusHistory;
  }
};

export const getLocalSyncPayload = async <E extends SyncEntityType>(
  db: LeitourDatabase,
  entityType: E,
  entityId: string
): Promise<SyncPayloadMap[E] | undefined> => {
  if (entityType === 'user_preference') {
    const settings = await db.settings.get('app');
    if (!settings) return undefined;
    if (entityId === 'app') return {
      id: 'app', showHomeHighlight: settings.showHomeHighlight ?? true
    } as SyncPayloadMap[E];
    if (entityId === 'bible_book_order' && settings.bibleBookOrder) return {
      id: 'bible_book_order', bookOrder: settings.bibleBookOrder
    } as SyncPayloadMap[E];
    return undefined;
  }
  const value = await tableFor(db, entityType).get(entityId) as SyncPayloadMap[E] | undefined;
  if (entityType === 'reading_session' && value && (value as ReadingSession).status !== 'completed') return undefined;
  return value ? projectLocalSyncPayload(db, entityType, value) : undefined;
};

export const projectLocalSyncPayload = async <E extends SyncEntityType>(
  db: LeitourDatabase,
  entityType: E,
  value: SyncPayloadMap[E],
  projectedDeletedSessionIds: ReadonlySet<string> = new Set()
): Promise<SyncPayloadMap[E]> => {
  if (entityType !== 'reading_progress_entry') return projectSyncPayload(entityType, value);
  const entry = value as ReadingProgressEntry;
  const referencedSessionExists = !entry.sessionId
    || (!projectedDeletedSessionIds.has(entry.sessionId) && Boolean(await db.sessions.get(entry.sessionId)));
  return projectReadingProgressEntryForSync(entry, referencedSessionExists) as SyncPayloadMap[E];
};

const addEntities = async <E extends Exclude<SyncEntityType, 'user_preference'>>(
  db: LeitourDatabase,
  result: LocalSyncEntity[],
  entityType: E,
  values: SyncPayloadMap[E][]
): Promise<void> => {
  for (const value of values) {
    if (entityType === 'reading_session' && (value as ReadingSession).status !== 'completed') continue;
    const payload = await projectLocalSyncPayload(db, entityType, value);
    result.push({
      entityType, entityId: value.id, payload,
      payloadVersion: payloadVersionForSyncPayload(entityType, payload),
      payloadHash: await Dexie.waitFor(hashSyncPayloadForEntity(entityType, payload))
    });
  }
};

export const listLocalSyncEntities = async (db: LeitourDatabase): Promise<LocalSyncEntity[]> => {
  const result: LocalSyncEntity[] = [];
  await addEntities(db, result, 'series', await db.series.toArray());
  await addEntities(db, result, 'book', await db.books.toArray());
  await addEntities(
    db,
    result,
    'reading_session',
    (await db.sessions.toArray()).filter((session): session is Extract<ReadingSession, { status: 'completed' }> =>
      session.status === 'completed')
  );
  await addEntities(db, result, 'reading_progress_entry', await db.progressEntries.toArray());
  await addEntities(db, result, 'book_status_history', await db.statusHistory.toArray());
  await addEntities(db, result, 'note', await db.notes.toArray());
  await addEntities(db, result, 'quote', await db.quotes.toArray());
  await addEntities(db, result, 'goal', await db.goals.toArray());
  const settings = await db.settings.get('app');
  const preferenceHasRemoteState = Boolean(await db.syncEntityStates.get(['user_preference', 'app']));
  const preferenceHasLocalIntent = await db.syncOutbox
    .filter(({ status, batch }) => status !== 'superseded'
      && batch.mutations.some(({ entityType, entityId }) =>
        entityType === 'user_preference' && entityId === 'app'))
    .count() > 0;
  if (settings && (preferenceHasRemoteState || preferenceHasLocalIntent)) {
    const payload: SyncPayloadMap['user_preference'] = {
      id: 'app', showHomeHighlight: settings.showHomeHighlight ?? true
    };
    result.push({
      entityType: 'user_preference', entityId: 'app', payload,
      payloadVersion: 1,
      payloadHash: await Dexie.waitFor(hashSyncPayloadForEntity('user_preference', payload))
    });
  }
  const bibleOrderHasRemoteState = Boolean(await db.syncEntityStates.get(['user_preference', 'bible_book_order']));
  const bibleOrderHasLocalIntent = await db.syncOutbox
    .filter(({ status, batch }) => status !== 'superseded'
      && batch.mutations.some(({ entityType, entityId }) =>
        entityType === 'user_preference' && entityId === 'bible_book_order'))
    .count() > 0;
  if (settings?.bibleBookOrder && (bibleOrderHasRemoteState || bibleOrderHasLocalIntent)) {
    const payload: SyncPayloadMap['user_preference'] = {
      id: 'bible_book_order', bookOrder: settings.bibleBookOrder
    };
    result.push({
      entityType: 'user_preference', entityId: 'bible_book_order', payload,
      payloadVersion: 1,
      payloadHash: await Dexie.waitFor(hashSyncPayloadForEntity('user_preference', payload))
    });
  }
  return result;
};

export const createRemoteConflict = (
  row: SyncSnapshotEntity | SyncChange,
  reason: SyncConflict['reason'],
  localPayload: unknown,
  localPayloadHash: string | undefined,
  detectedAt = new Date().toISOString()
): SyncConflict => {
  const remoteDeletedAt = row.operation === 'delete'
    ? ('deletedAt' in row && row.deletedAt
        ? row.deletedAt
        : 'changedAt' in row
          ? row.changedAt
          : undefined)
    : undefined;
  return {
    id: crypto.randomUUID(), status: 'open', entityType: row.entityType, entityId: row.entityId,
    reason, baseRevision: { state: 'known', revision: row.revision },
    localPayload, localPayloadHash, remoteRevision: row.revision,
    remotePayload: row.payload, remotePayloadHash: row.payloadHash,
    remotePayloadVersion: row.payloadVersion,
    ...(remoteDeletedAt ? { remoteDeletedAt } : {}),
    detectedAt
  };
};

export const applyRemoteEntity = async (
  db: LeitourDatabase,
  row: SyncSnapshotEntity | SyncChange,
  options: { preserveLocal: boolean; conflictReason?: SyncConflict['reason'] } = { preserveLocal: false }
): Promise<SyncConflict | undefined> => {
  const preflight = await preflightRemoteEntity(db, row, options);
  if (preflight) return preflight;

  if (row.operation === 'delete') {
    if (row.entityType === 'user_preference') {
      const current = await db.settings.get('app');
      if (current && row.entityId === 'app') await db.settings.put({ ...current, showHomeHighlight: true });
      else if (current && row.entityId === 'bible_book_order') {
        const next = { ...current };
        delete next.bibleBookOrder;
        await db.settings.put(next);
      }
    } else {
      await tableFor(db, row.entityType).delete(row.entityId);
    }
    return undefined;
  }

  if (row.entityType === 'book') {
    const current = await db.books.get(row.entityId);
    const remote = row.payload as Book;
    await db.books.put(current?.coverAssetId ? { ...remote, coverAssetId: current.coverAssetId } : remote);
  } else if (row.entityType === 'user_preference') {
    const current = await db.settings.get('app');
    const remote = canonicalUserPreferencePayload(row.payload);
    if (!remote || remote.id !== row.entityId) throw new Error('Payload remoto de preferência inválido.');
    const now = new Date().toISOString();
    const base: AppSettings = current ?? {
      id: 'app', theme: 'system', locale: 'pt-BR', showHomeHighlight: true,
      createdAt: now, updatedAt: now
    };
    const settings: AppSettings = remote.id === 'app'
      ? { ...base, showHomeHighlight: remote.showHomeHighlight, updatedAt: now }
      : { ...base, bibleBookOrder: remote.bookOrder, updatedAt: now };
    await db.settings.put(settings);
  } else {
    await tableFor(db, row.entityType).put(row.payload);
  }
  return undefined;
};

export const preflightRemoteEntity = async (
  db: LeitourDatabase,
  row: SyncSnapshotEntity | SyncChange,
  options: { preserveLocal: boolean; conflictReason?: SyncConflict['reason'] } = { preserveLocal: false }
): Promise<SyncConflict | undefined> => {
  if (options.preserveLocal) {
    const localPayload = await getLocalSyncPayload(db, row.entityType, row.entityId);
    const localPayloadHash = localPayload ? await hashSyncPayloadForEntity(row.entityType, localPayload) : undefined;
    return createRemoteConflict(row, options.conflictReason ?? 'bootstrap_divergence', localPayload, localPayloadHash);
  }

  if (row.entityType === 'reading_session') {
    const current = await db.sessions.get(row.entityId);
    if (current && current.status !== 'completed') {
      return createRemoteConflict(row, 'invalid_local_state', current, undefined);
    }
  }
  if (row.entityType === 'book') {
    const open = await db.sessions.where('bookId').equals(row.entityId)
      .filter(({ status }) => status !== 'completed').first();
    if (open && row.operation === 'delete') {
      const localPayload = await getLocalSyncPayload(db, row.entityType, row.entityId);
      return createRemoteConflict(row, 'invalid_local_state', localPayload, undefined);
    }
  }
  return undefined;
};
